import { AppData, Lightning, Log } from "@lightningjs/sdk";
import { ItemNode } from "../lib/components/grid/ItemNode";
import { AppSetup } from "../lib/app-setup";
import GridViewModel from "../lib/components/grid/grid-view-model";
import { FTStreamingPlayer } from "../lib/components/ft-streaming-player";
import { Platform } from "../platforms/platform";
import RowList from "../lib/components/grid/row-model";
import { PLAYERSTATE } from "../lib/enum";
import { events as ftEvents } from "../lib/components/ft-streaming-player/utils";
import EPGManager from "../lib/components/epg/epg-model";
import EPGGuide from "../lib/components/epg/views/epg-guide";
import { Logger } from "../lib/logger";
import { PreviousVodInfo } from "../lib/models/navigation-stack";
import AppModel from "../lib/models/app-model";
import { KeyCodes } from "../lib/key-codes";
import LiveHeader from "../ui-elements/live-header";
import ChannelListOverlay from "../lib/components/epg/views/channel-list-overlay";
import EPGUtility from "../lib/components/epg/epg-utility";

export interface VideoScreenLiveTypeConfig extends Lightning.Component.TypeConfig {
    IsPage: true;
}

export interface VideoScreenLiveTemplateSpec extends Lightning.Component.TemplateSpecLoose {
    Container: {
        ControlWrapper: {},
    }
}

export default class VideoScreenLive extends Lightning.Component<VideoScreenLiveTemplateSpec, VideoScreenLiveTypeConfig> implements Lightning.Component<VideoScreenLiveTemplateSpec>
{

    videoEl!: HTMLVideoElement | null
    platform!: Platform
    ftStreamingPlayer!: FTStreamingPlayer
    channelIndex = 0
    channelRow!: RowList
    grid!: GridViewModel
    videoInfo!: ItemNode
    playerState: any;
    currentTimeExit: number = 0
    isDRM: boolean = false
    isHLS: boolean = false
    isMP4: boolean = false
    videoUrl: string = ''
    changeChannelTimerId: any;
    CHANNEL_CHANGE_DELAY = 1000;
    SHOW_CONTROL_DELAY = 5000;
    showControlTimerId: any;

    channelStartTimestamp

    epgManager: EPGManager | null = null
    _rowSwitchInProgress = false
    _epgShownOnEntry = false
    videoElWrapper: HTMLElement;


    static override _template(): Lightning.Component.Template<Lightning.Component.TemplateSpecLoose> {
        return {
            Container: {
                w: 1920,
                h: 1080,
                collision: 2,
                HitArea: {
                    w: 1920,
                    h: 1080,
                    rect: true,
                    color: 0x00000000,
                    collision: true,
                    zIndex: 0
                },
                Header: {
                    type: LiveHeader,
                    zIndex: 10,
                    alpha: 0
                },
                EPGGuide: {
                    y:1080,
                    w:1920,
                    h:822,
                    type: EPGGuide
                },
                ChannelListOverlay: {
                    type: ChannelListOverlay,
                    zIndex: 100,
                    visible: false
                }
            }
        }
    }


    /** setup the epg guide on the video inisitlization
     * will get the epg manager from app-model where
     * it returns the value epgmanager if it is the same url.
     * initializing the epgmanager and listern to the events.
     * @inheritdoc
     */
    override _init() {
        const { appState } = AppData as AppSetup
        const app = appState?.getApp()
        this.grid = app?.navigation.getCurrentGrid()!
        this.channelIndex = this.grid.getFocusInfo().foucsItem
        this.channelRow = this.grid.getRowAt(this.grid.getFocusInfo().focusRow)!
        this.channelRow.fetchMoreVideos(this.channelRow.totalPages - 1) //loading the last page for rotating the channels
        Logger.printLog('data', '_init: this.channelRow ', this.channelRow);
        this._setupVideoPlayer()
        this._setupEpg()
        
    }

    override _firstActive() {
        this.videoInfo = this.grid.getFocusedItem()
        this.upateChannelFocus(true)
        this.epgManager.init(this.channelIndex)
        this.showEpgGuide()
        if (!this.epgManager.loaded) {
            ;(this.tag('EPGGuide') as any)?.setLoading(true)
        }
    }

    _setupEpg() {
        const { appState } = AppData as AppSetup
        const app = appState?.getApp()

        this.epgManager = app?.loadEpg(this.channelRow.url, [this.channelRow], this.channelIndex)!
        this.epgManager.on('onEpgLoaded', this.onEpgLoaded.bind(this))
        this.epgManager.on('onCurrentTime', this.onEpgCurrentTime.bind(this))
        this.epgManager.on('onProgramChanged', this.onEpgProgramChange.bind(this))
    }


    /**
     * clear the resources for the video screen and goes back to the
     * previous page.
     */
    override _detach() {
        if(this.epgManager) {
            this.epgManager.removeAllListeners('onEpgLoaded')
            this.epgManager.removeAllListeners('onCurrentTime')
            this.epgManager.removeAllListeners('onProgramChanged')
            this.epgManager.destroy()
            this.epgManager = null;
        }
        this.resetPlayer();
        this._epgShownOnEntry = false
        this.stage.gc()
    }

    /**
     * Event fires when the EPG is initialized and the current selected epg is available.
     */
    onEpgLoaded() {
        Log.info('EPG Loaded')
        const allRowNames = this.grid?.uiList
            ? this.grid.uiList.map((uiRow: any) => {
                if (uiRow.data && uiRow.data.item && uiRow.data.item.rowList) {
                    const rowList = uiRow.data.item.rowList;
                    if (rowList.row_type === 'live') {
                        return rowList.name || '';
                    }
                    return '';
                }
                if (uiRow.title) return uiRow.title;
                return '';
            }).filter(Boolean)
            : [];

        // Use the EPGManager's current row name so row-switches are reflected correctly
        const activeRowName = this.epgManager?.channelRowList?.name || this.channelRow?.name || '';
        const epgGuide = this.tag('EPGGuide');

        // setActiveRowNameSilent bypasses the full setter so _update() isn't called
        // mid-patch — avoids a redundant channel-list rebuild before allRowNames fires.
        epgGuide.epgChannelCreated = false;
        epgGuide.setActiveRowNameSilent(activeRowName);

        epgGuide.patch({ allRowNames, epg: this.epgManager });

        // Safety net: if both references were unchanged the Base setters didn't fire.
        if (!epgGuide.epgChannelCreated) {
            epgGuide._update();
        }

        // Seed the look-ahead window from the initial active row — same as
        // MultiRowGrid._updateAndFetchMoreRows() which calls checkAndLoadMoreRows on init
        const activeIndex = this.grid?.uiList?.findIndex((uiRow: any) =>
            uiRow?.data?.item?.rowList?.name === activeRowName
        ) ?? 0
        if (this.grid && activeIndex >= 0 && activeIndex < this.grid.uiList.length) {
            this.grid.checkAndLoadMoreRows(activeIndex)
        }

        if (!this._epgShownOnEntry) {
            this._epgShownOnEntry = true
        }

        if (this._rowSwitchInProgress) {
            // Row switch: user was browsing the expanded EPG — keep it expanded.
            // resetFocusToThumbnail() would enter ThumbnailState whose $enter fires
            // $expandEpg(false), collapsing the guide. Skip it and stay in GridState.
            ;(this.tag('EPGGuide') as any)?.enterGridState()
        } else {
            this.tag('EPGGuide').resetFocusToThumbnail()
        }

        ;(this.tag('EPGGuide') as any)?.setLoading(false)
    }

    $epgRowLabelFocused(index: number) {
        if (this.grid && index >= 0 && index < this.grid.uiList.length) {
            this.grid.checkAndLoadMoreRows(index)
        }
    }

    /**
     * will be dispatched when the current time is changed
     * will be updated every minute.
     *
     * @param {CustomEvent} event
     */
    onEpgCurrentTime(event:CustomEvent) {
        Log.info('EPG Timeupdate: ', event)
        this.tag('EPGGuide')._updateCurrentTime(event.detail.currDate)
    }


    /**
     * will be dispatched when the program changes within a channel or if channel changes.
     * CustomEvent will have previous program details and next program change details.
     * will be used for logging the live video logging.
     *
     * @param {CustomEvent} event
     */
    onEpgProgramChange(event:CustomEvent) {
        Log.info('EPG onProgramChanged: ', event)
        const {prev, next}  = event.detail.channelInfo;

        const { appState} = AppData as AppSetup;
        const app = appState?.getApp();
        let previousVodInfo:PreviousVodInfo = app.navigation.getPreviousVideoInfo()!;
        if (previousVodInfo) {
            let wd = Math.round((new Date().getTime() - this.channelStartTimestamp)/1000)
            if(wd > previousVodInfo.vodInfo.runtime) {
                wd = previousVodInfo.vodInfo.runtime
            }
            app?.navigation.setPreviousVideoWatchDuration(wd)
            Logger.logNextPrevVideoView(next, previousVodInfo);
        } else {
            Logger.logNewVideoView(next);
        }
        this.channelStartTimestamp = new Date().getTime()
        app?.navigation.setPreviousVideoInfo(next)
        this.tag('EPGGuide').updateEpgChannel(next)
    }

    showEpgGuide() {
        this._setState('EpgGuideState')
        this.tag('EPGGuide').resetFocusToThumbnail()
    }

    showControls() {
        this.$hideEpgGuide()
        clearInterval(this.showControlTimerId)
    }

    hideControls() {
        clearInterval(this.showControlTimerId)
    }

    changeChannel(forcePlay = false) {
        clearTimeout(this.changeChannelTimerId);
        this.changeChannelTimerId = setTimeout(() => {
            this.epgManager.changeChannel(this.channelIndex)
            const prevVideoInfoNodeId = this.videoInfo.node_id
            this.videoInfo = this.channelRow.videos[this.channelIndex]!
            if(forcePlay || (prevVideoInfoNodeId !== this.videoInfo?.node_id)) {
                //this.resetPlayer()
                this.resumePlayer()
            }
        }, this.CHANNEL_CHANGE_DELAY)

        return true
    }

    _getActionKey() {
        if (this.videoInfo) {
            return `${this.videoInfo.node_id}_${Math.round((new Date().getTime() - this.channelStartTimestamp)/1000)}`
        }
        return ''
    }

    //----------------Video player setup and handling code section.
    _setupVideoPlayer() {
        document.getElementById('content-player')?.remove()
        document.getElementById('content-player-wrapper')?.remove()
        const elw = document.createElement('div')
        elw.setAttribute('id', 'content-player-wrapper')
        elw.setAttribute('style', 'position: absolute;top: 0px;left: 0px;width: 100%;height: 100%;background-color: #000;display:none;')
        const el = document.createElement('video')
        el.id = 'content-player'
        el.style.width = '100%'
        el.style.height = '100%'
        el.setAttribute('crossorigin', 'anonymous')
        el.setAttribute('autoplay', 'false')
        elw.appendChild(el)
        const subtitle = document.createElement('div')
        subtitle.id = 'subtitle'
        subtitle.setAttribute('style', 'position: absolute;bottom: 90px;left: 10px;text-align: center;max-width: 1900px;right: 10px;display: none;')
        subtitle.innerHTML = '<span></span>'
        elw.appendChild(subtitle)
        document.body.appendChild(elw)

        this.videoEl = el as HTMLVideoElement
        this.videoElWrapper = elw as HTMLElement
        const options = {
            debug: false,
            enableWebVTT: true,
            liveMaxLatencyDurationCount: 10,//sec
            liveSyncDurationCount: 5,
            lowLatencyMode: true,
            startLevel:0,
            backBufferLength: 5
        }
        this.ftStreamingPlayer = new Hls(options)
        this.ftStreamingPlayer.attachMedia(this.videoEl)
        this.ftStreamingPlayer.on(Hls.Events.MANIFEST_PARSED, () => {
            this.videoEl.play()
          });
        //this.ftStreamingPlayer = FTStreamingPlayer.getInstance(this.videoEl)
        this._setEventListeners()
    }

    _setEventListeners() {
        ftEvents.forEach(event => {
            this.videoEl.addEventListener(event, this._handleFtPlayerEvents)
        })
        this.videoEl.addEventListener('playererror', this._handleFtPlayerEvents)
    }

    _removeEventListener() {
        ftEvents.forEach(event => {
            this.videoEl.removeEventListener(event, this._handleFtPlayerEvents)
        })
        this.videoEl.removeEventListener('playererror', this._handleFtPlayerEvents)
    }

    _handleFtPlayerEvents = (event: any) => {
        if (event.type !== 'progress' && event.type !== 'timeupdate') { Log.info('PlayerEvent:', event.type) }
        switch (event.type) {
            case 'loadedmetadata':
                this.setPlayerState(PLAYERSTATE.READY)
                break;
            case 'pause':
                this.setPlayerState(PLAYERSTATE.PAUSED)
                break;
            case 'waiting':
                this.setPlayerState(PLAYERSTATE.PLAYING)
                break;
            case 'playing':
                this.setPlayerState(PLAYERSTATE.PLAYING)
                break;
            case 'ended':
                break;
            case 'error':
            case 'playererror':
                Log.info('playererror: ', event)
                this.handlePlayerError(event)
                break;
            case 'timeupdate':
                break;
        }
    }

    getErrorCode(error: any) {
        let code = '';
        try {
            code = error.detail.message.error.code
        } catch (e) {

        }
        return code;
    }

    getErrorMsg(error: any) {
        let msg = '';
        try {
            msg = error.detail.message.error.message
        } catch (e) {

        }
        return msg;
    }

    getErrorXhr(error: any) {
        try {
            return error.detail.message.error.data
        } catch (e) {
            return null
        }
    }

    handlePlayerError(error: CustomEvent) {
        let errType = '';
        let errorDetailStr = '';
        let isFatal = false
        const { appState, platform } = AppData as AppSetup;
        try {
            if (error) {
                if (error.detail && error.detail.type) {
                    if (error.detail.type == 'M(PEG)-DASH') {
                        let errorDetail = {
                            type: 'dashError',
                            message: this.getErrorMsg(error),
                            errorCode: this.getErrorCode(error)
                        }
                        errType = 'Dash';
                        errorDetailStr = JSON.stringify(errorDetail);
                        isFatal = true
                    } else if (error.detail.type == "HLS") {
                        errType = error.detail.type;
                        var errorDetail = {
                            type: error.detail.type,
                            message: error.detail.message
                        }
                        errorDetailStr = JSON.stringify(errorDetail);
                        isFatal = error.detail.data[1].fatal
                    } else if (error.detail.type == "MP4") {
                        isFatal = true
                        errType = error.detail.type;
                    }
                }
                Logger.processUserAction('playbackfailed', this._getActionKey(), errType, errorDetailStr);
                //platform?.networkManager.healthCheck(true, this, { success: null, failed: null }) // currently disabling the health check.
                if (isFatal) {
                    Log.info('Video Error: ', error)
                    //XFIN-703 -  sendin gthe error metrics to platform which in turn will send the metrics to appropriate device api
                    // example for comcast it will send the error metrics to Firebolt.
                    platform?.sendMetrics('error', {errorType: 'network', code: errType, description:errorDetailStr})
                    //dataObj.errorType, dataObj.code, dataObj.description
                }
            }
        } catch (e) {

        }
    }

    setPlayerState(playerState: PLAYERSTATE) {
        const { appState } = AppData as AppSetup
        if (playerState == PLAYERSTATE.READY) {
            appState.eventInProgress = false;
        } else if (playerState == PLAYERSTATE.PLAYING) {

        } else if (playerState == PLAYERSTATE.PAUSED) {

        } else if (playerState == PLAYERSTATE.FF) {

        } else if (playerState == PLAYERSTATE.REW) {

        }
        this.playerState = playerState
    }

    resetPlayer() {
        this._removeEventListener()
        this.ftStreamingPlayer.detachMedia()
        this.ftStreamingPlayer.destroy()
        this.setPlayerState(PLAYERSTATE.NONE)
        this.hideVideo()
    }

    resumePlayer(resumeTime = 0) {
        Log.info("videoInfo ", this.videoInfo);
        this.videoUrl = this.videoInfo.video_url!;

        const { appState} = AppData as AppSetup;
        const app:AppModel = appState?.getApp()!
        app.imaAdHandler.currentVodInfo = this.videoInfo
        this.videoUrl = app.imaAdHandler.replaceMacros(this.videoUrl)

        Log.info("videoUrl ", this.videoUrl);
        this.setPlayerState(PLAYERSTATE.INITIALIZING)
        this.ftStreamingPlayer.loadSource(this.videoUrl)
        this.showVideo()
    }

    hideVideo() {
        //VideoPlayer.hide();
        if ($('#content-player-wrapper')) {
            $('#content-player-wrapper').hide()
        }
    }

    showVideo() {
        if ($('#content-player-wrapper')) {
            $('#content-player-wrapper').show()
        }
    }

    upateChannelFocus(forcePlay = false) {
        const focusInfo = this.grid.getFocusInfo()
        focusInfo.foucsItem = this.channelIndex
        this.grid.changeFocus(focusInfo)
        this.showControls()
        this.changeChannel(forcePlay)
    }

    $changeChannel(channelIndex) {
        //console.log('EPG $changeChannel', channelIndex)
        this.channelIndex = channelIndex;
        (this.tag('EPGGuide') as any)?.setNowPlayingIndex(channelIndex);
        // Update grid focus and trigger channel change without hiding the EPG —
        // just reset the auto-hide timer so the user can keep browsing.
        const focusInfo = this.grid.getFocusInfo()
        focusInfo.foucsItem = this.channelIndex
        this.grid.changeFocus(focusInfo)
        ;(this.tag('EPGGuide') as any)?.startAutoHideTimeout()
        this.changeChannel()
    }

    $channelListSelectChannel(index: number) {
        this.channelIndex = index;
        (this.tag('EPGGuide') as any)?.setNowPlayingIndex(index);
        const focusInfo = this.grid.getFocusInfo()
        focusInfo.foucsItem = this.channelIndex
        this.grid.changeFocus(focusInfo)
        this.changeChannel(true);
        this.$closeChannelList();
    }

    /**
     * Fired by EPGGuide when the user selects a different row in the left side column.
     * Fetches channels for that row and reloads the EPG guide.
     */
    async $epgRowChanged(newRowName: string) {
        if (!this.grid || !this.epgManager) return;
        if (this._rowSwitchInProgress) return;
        this._rowSwitchInProgress = true;
        ;(this.tag('EPGGuide') as any)?.setLoading(true, false)

        try {
            // Find the RowList that matches the selected row name
            const rowIndex = this.grid.uiList?.findIndex((uiRow: any) =>
                uiRow?.data?.item?.rowList?.name === newRowName
            ) ?? -1;
            const newRowList = rowIndex >= 0 ? this.grid.uiList[rowIndex].data.item.rowList as RowList : null;

            if (!newRowList) {
                Log.warn('$epgRowChanged: Could not find RowList for row name:', newRowName);
                return;
            }

            // Ensure the row's channels are loaded before handing off to EPGManager.
            // Always fetch page 0 if not yet initialized — background preload may not have
            // finished if the user switches rows quickly. fetchedPagePromise deduplicates.
            if (!newRowList.isInitialized) {
                await newRowList.fetchMoreVideos(0);
            }

            // Keep channelRow in sync so changeChannel() reads video_url from the
            // correct row — same pattern the old updateUI() used with channelRow.videos[index].
            this.channelRow = newRowList;
            this.channelIndex = 0;

            // Keep the underlying main grid's focus pointed at the newly active row so
            // that navigating back lands focus on the right row+item, not the row the
            // user was on before switching EPG categories.
            const focusInfo = this.grid.getFocusInfo();
            focusInfo.focusRow = rowIndex;
            focusInfo.foucsItem = 0;
            this.grid.changeFocus(focusInfo);

            // Switch the EPGManager to the new row — this fires 'onEpgLoaded' when done
            // which calls onEpgLoaded() and refreshes the EPGGuide automatically
            this.tag('EPGGuide').patch({ activeRowName: newRowName });
            await this.epgManager.switchRow(newRowList, 0);
        } finally {
            this._rowSwitchInProgress = false;
        }
    }

    hitCloseTracker() {
        const { appState} = AppData as AppSetup;
        const app = appState?.getApp();
        let previousVodInfo:PreviousVodInfo = app?.navigation.getPreviousVideoInfo()!;
        if (previousVodInfo) {
            let wd = Math.round((new Date().getTime() - this.channelStartTimestamp)/1000)
            // if(previousVodInfo.vodInfo.runtime < wd) {
            //     wd = previousVodInfo.vodInfo.runtime
            // }
            app?.navigation.setPreviousVideoWatchDuration(wd)
            Logger.logCloseVideoView(previousVodInfo);
            app.navigation.resetPreviousVideoInfo()
        }
    }

    closeVideo() {
        const { appState } = AppData as AppSetup;
        const app = appState?.getApp();
        this.hitCloseTracker()
        this.resetPlayer()
    }

    override _handleBack() {
        const { appState } = AppData as AppSetup;
        const app = appState?.getApp();
        this.closeVideo()
        app?.handleBackNavigation();
        return true
    }

    override _getFocused() {
        return this
    }

    override _captureKey(): boolean | void {
        this._resetHoverTarget()
        return false
    }

    override _handleKey(e: KeyboardEvent): boolean | void {
        switch (e.keyCode) {
            //media controls key
            case KeyCodes.KEY_NEXT:
                this._handleDown()
                break;
            case KeyCodes.KEY_PREVIOUS:
                this._handleUp()
                break;
            case KeyCodes.KEY_EXIT:
                this.hitCloseTracker();
                Logger.processUserAction('userExitedViaRemoteDirectly', this._getActionKey());
                break;
            case KeyCodes.KEY_HOME:
                this.hitCloseTracker();
                Logger.processUserAction('userExitedViaRemoteDirectly', this._getActionKey());
                break;
        }
    }

    $hideEpgGuide() {
        this._setState('')
    }

    _resetHoverTarget() {
        ;(this.application as any).__hoveredChild = null
    }

    $onHeaderBackClick() {
        const { appState } = AppData as AppSetup;
        const app = appState?.getApp();
        this.closeVideo()
        app?.handleBackNavigation();
    }

    $openChannelList() {
        const channels = this.channelRow?.videos ?? [];
        const currDate = this.epgManager?.currDate ?? new Date();
        const activeRowName = this.epgManager?.channelRowList?.name || '';
        const epgChannels = this.epgManager?.EPGChannelList ?? [];

        // Match EPG channels to the active row, same filtering EPGGuide uses
        const filteredEpg = activeRowName
            ? epgChannels.filter((ch: any) => ch.rowName === activeRowName)
            : epgChannels;

        const items = channels.map((ch: any, idx: number) => {
            const epgCh = filteredEpg[idx];
            const program = epgCh?.getNowPlayingProgram(currDate);
            let progress = 0;
            if (program?.start_timeObj && program?.end_timeObj) {
                const dur = program.end_timeObj.getTime() - program.start_timeObj.getTime();
                const cur = currDate.getTime() - program.start_timeObj.getTime();
                progress = dur > 0 ? Math.max(0, Math.min(1, cur / dur)) : 0;
            }
            return {
                iconUrl: ch.squareIcon || ch.main_picture || ch.image || '',
                mainPicture: ch.main_picture || '',
                channelName: ch.title || '',
                programName: program?.title || '',
                programImage: program?.image || '',
                rating: program?.rating || '',
                category: program?.category || '',
                description: program?.description || '',
                startTime: program?.start_timeObj ? EPGUtility.formatAMPM(program.start_timeObj) : '',
                endTime: program?.end_timeObj ? EPGUtility.formatAMPM(program.end_timeObj) : '',
                progress
            };
        });

        const overlay = this.tag('ChannelListOverlay') as any;
        overlay.setChannels(items, this.channelIndex);
        overlay.setEpgContext(this.epgManager, filteredEpg, currDate);
        overlay.currentChannelName = channels[this.channelIndex]?.title || '';
        this._setState('ChannelListState')
    }

    $closeChannelList() {
        this._setState('')
    }

    $expandEpg(isExpanded: boolean) {
        this.tag('EPGGuide').y = isExpanded ? 258 : 638
        this.tag('EPGGuide').tag('CurrTimeLine')?.patch({ h: isExpanded ? 500 : 120 })
    }

    static override _states() {
        return [
            class EpgGuideState extends this {
                override $enter(event) {
                    this.tag('EPGGuide').y = 638
                    this.tag('Header')?.patch({ alpha: 1 })
                }
                override $exit(event) {
                    this.tag('EPGGuide').y = 1080
                    this.tag('Header')?.patch({ alpha: 0 })
                    this._resetHoverTarget()
                }
                override _getFocused() {
                    return this.tag('EPGGuide')
                }
                override _handleKey() {
                    return true
                    // Overwrites root function when we are on this state
                }
                override _handleBack() {
                    this._setState('')
                    return true
                }
            },

            class ChannelListState extends this {
                override $enter() {
                    this.tag('EPGGuide').y = 1080
                    this.tag('Header')?.patch({ alpha: 0 })
                    this.tag('ChannelListOverlay').visible = true
                }
                override $exit() {
                    this.tag('ChannelListOverlay').visible = false
                    this._resetHoverTarget()
                }
                override _getFocused() {
                    return this.tag('ChannelListOverlay')
                }
                override _handleBack() {
                    this.$closeChannelList()
                    return true
                }
            }
        ]
    }

    _handleHover() {
        if (this._getState() === '') {
            this.showEpgGuide()
        }
        return true
    }

    _handleClick() {
        if (this._getState() === '') {
            this.showEpgGuide()
        }
        return true
    }

    // When EPG is hidden, any arrow or enter key brings it back
    override _handleDown() {
        this.showEpgGuide()
        return true
    }

    override _handleRight() {
        this.showEpgGuide()
        return true
    }

    override _handleLeft() {
        this.showEpgGuide()
        return true
    }

    override _handleUp() {
        this.showEpgGuide()
        return true
    }

    override _handleEnter() {
        this.showEpgGuide()
        return true
    }
}
